import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    fetchNotifications,
    fetchNotificationSummary,
    markAllNotificationsRead,
    markNotificationRead,
    type PortalNotification,
} from '@/api/shell';
import { formatNotificationAge, notificationTone } from './shellFormat';
import './NotificationPopover.css';

/** The popover is a peek, not the archive — "Xem tất cả" leads to Nhật ký. */
const NOTIFICATION_PAGE_SIZE = 8;

const SUMMARY_KEY = 'notification-summary';
const LIST_KEY = 'notifications';

interface NotificationPopoverProps {
    /**
     * Classes for the bell button itself. The two topbars style their icon
     * buttons differently (`AppBar` has a bordered 34px bell, `ConsoleTopbar` a
     * ghost 32px one), so the trigger's chrome is passed in rather than forked
     * into two near-identical components.
     */
    triggerClassName: string;
    /** Glyph size — 16 in `AppBar`, 18 in `ConsoleTopbar`, matching each frame. */
    iconSize?: number;
}

/**
 * EAA-1813: the notification centre (handoff frame 14), replacing the `disabled`
 * topbar bell that shipped with an sr-only "chưa khả dụng" note. Backed by
 * `/api/v1/me/notifications*` (EAA-1831) via `src/api/shell.ts`.
 *
 * This component owns the bell as well as the panel: outside-click and Escape
 * both need the trigger and the dropdown inside one container ref, following the
 * pattern `UserMenu` already establishes for the portal's popovers.
 *
 * Honest-degrade notes against frame 14:
 *   - the row dot is keyed on `notification_type`, not on a severity field — the
 *     model has none (see `shellFormat.notificationTone`);
 *   - SCREENS.md's one-line summary says "grouped by kind", but the frame itself
 *     draws a single flat, newest-first list. The frame is canonical (the same
 *     precedent tokens.css records for frame 09), and the API returns one
 *     ordered page with no grouping key, so the list is flat and the dot carries
 *     the kind.
 */
export function NotificationPopover({ triggerClassName, iconSize = 18 }: NotificationPopoverProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const can = useCan();

    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const enabled = authenticated && Boolean(accessToken);

    // The badge has to be right without the popover ever being opened, so the
    // summary query runs whenever the topbar is mounted. The list is the
    // expensive half and only runs while the panel is actually on screen.
    const summaryQuery = useQuery({
        queryKey: [SUMMARY_KEY, accessToken],
        queryFn: () => fetchNotificationSummary(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });

    const listQuery = useQuery({
        queryKey: [LIST_KEY, accessToken],
        queryFn: () =>
            fetchNotifications(config.apiBase, accessToken as string, {
                pageSize: NOTIFICATION_PAGE_SIZE,
            }),
        enabled: enabled && open,
        retry: false,
    });

    // EAA-1813: refetch the count on open rather than polling. This app has no
    // polling anywhere, and adding an interval here would be a new data-fetching
    // pattern for one badge; between opens the shared queryClient's 30s
    // staleTime plus TanStack's refetch-on-window-focus already keep it fresh.
    useEffect(() => {
        if (!open) return;
        void queryClient.invalidateQueries({ queryKey: [SUMMARY_KEY] });
    }, [open, queryClient]);

    // Outside-click + Escape, mirroring UserMenu's single guarded effect.
    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const unreadCount = summaryQuery.data?.unread_count ?? 0;

    /**
     * EAA-1813: `/me/notifications/summary` is cached 30s server-side and
     * invalidated asynchronously on mark-read (api/shell.ts says so explicitly),
     * so a refetch fired right after a mutation can still return the pre-read
     * count and the badge would visibly un-read itself. Writing the mutation's
     * known post-condition straight into the cache avoids that window — it is
     * the documented result of the call, not a guess.
     */
    const setUnreadCount = useCallback(
        (next: number) => {
            queryClient.setQueryData<{ unread_count: number }>([SUMMARY_KEY, accessToken], {
                unread_count: Math.max(next, 0),
            });
        },
        [queryClient, accessToken]
    );

    const markReadMutation = useMutation({
        mutationFn: (id: string) => markNotificationRead(config.apiBase, accessToken as string, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
        },
    });

    const markAllMutation = useMutation({
        mutationFn: () => markAllNotificationsRead(config.apiBase, accessToken as string),
        onSuccess: () => {
            setUnreadCount(0);
            void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
        },
    });

    const handleRowClick = (item: PortalNotification) => {
        if (!item.is_read) {
            setUnreadCount(unreadCount - 1);
            markReadMutation.mutate(item.id);
        }
        setOpen(false);
        // `target` is `''` for notifications that resolve nowhere — those still
        // mark read, they just do not navigate.
        if (item.target) navigate(item.target);
    };

    const items = listQuery.data?.results ?? [];

    let list: ReactNode;
    if (listQuery.isError) {
        list = (
            <p className="portal-notif-hint portal-notif-hint--error">
                {t('shell.notifications.loadError')}
            </p>
        );
    } else if (listQuery.isPending) {
        list = <p className="portal-notif-hint">{t('shell.notifications.loading')}</p>;
    } else if (items.length === 0) {
        list = <p className="portal-notif-hint">{t('shell.notifications.emptyState')}</p>;
    } else {
        list = items.map((item) => (
            <button
                key={item.id}
                type="button"
                className={'portal-notif-row' + (item.is_read ? '' : ' is-unread')}
                onClick={() => handleRowClick(item)}
            >
                <span
                    className={`portal-notif-dot portal-notif-dot--${notificationTone(item.notification_type)}`}
                    aria-hidden="true"
                />
                <span className="portal-notif-row-text">
                    <span className="portal-notif-row-title">{item.title}</span>
                    {item.body && <span className="portal-notif-row-body">{item.body}</span>}
                    <span className="portal-notif-row-time">
                        {formatNotificationAge(item.created_at)}
                        {!item.is_read && (
                            <span className="portal-sr-only">
                                {' '}
                                · {t('shell.notifications.unreadSrLabel')}
                            </span>
                        )}
                    </span>
                </span>
            </button>
        ));
    }

    let bellLabel = t('shell.notifications.title');
    if (unreadCount > 0) {
        bellLabel = t('shell.notifications.bellLabelUnread', { count: unreadCount });
    }

    return (
        <div className="portal-notif" ref={containerRef}>
            <button
                ref={triggerRef}
                type="button"
                className={`${triggerClassName} portal-notif-trigger`}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={bellLabel}
                title={bellLabel}
                onClick={() => setOpen((isOpen) => !isOpen)}
            >
                <BellIcon size={iconSize} />
                {unreadCount > 0 && (
                    <span className="portal-notif-count" aria-hidden="true">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="portal-notif-panel"
                    role="dialog"
                    aria-label={t('shell.notifications.title')}
                >
                    <div className="portal-notif-head">
                        <span className="portal-notif-title">{t('shell.notifications.title')}</span>
                        {unreadCount > 0 && (
                            <span className="portal-notif-pill">
                                {t('shell.notifications.newCountPill', { count: unreadCount })}
                            </span>
                        )}
                        <span className="portal-notif-head-spacer" />
                        <button
                            type="button"
                            className="portal-notif-link"
                            disabled={unreadCount === 0 || markAllMutation.isPending}
                            onClick={() => markAllMutation.mutate()}
                        >
                            {t('shell.notifications.markAllRead')}
                        </button>
                    </div>
                    <div className="portal-notif-list">{list}</div>
                    {/* EAA-1813: frame 14's footer links into Nhật ký, but
                        `/console/audit` is gated on `console.audit.view` —
                        SUPER-ADMIN ONLY (ORG_ADMIN_CONSOLE_CAPABILITIES is just
                        `console.orgs.view`). AuditPage returns null without it,
                        so for an org-admin an ungated link led to a blank
                        content panel. The bell itself is NOT gated: notifications
                        are `IsAuthenticated`, so every caller has real rows to
                        read — only this one link needs the capability.
                        Fail-closed side effect: `useCan()` has no provider on
                        /profile (AppBar renders outside ConsoleLayout), so the
                        link hides there even for a super-admin. Hiding a working
                        link beats offering a blank page. */}
                    {can('console.audit.view') && (
                        <div className="portal-notif-foot">
                            <Link
                                className="portal-notif-link"
                                to="/console/audit"
                                onClick={() => setOpen(false)}
                            >
                                {t('shell.notifications.viewAllInAuditLog')}
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* Inline icon — no icon dependency, matching the topbar/nav convention. */
function BellIcon({ size }: { size: number }) {
    return (
        <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" strokeLinejoin="round" />
            <path d="M10.5 20a2 2 0 0 0 3 0" strokeLinecap="round" />
        </svg>
    );
}
